import { FocusEventHandler } from 'react';
import Select, { components } from 'react-select';

import './dropdown.scss';

export type IOption = { label: string; value: any } | any;

type IMenuType = 'auto' | 'top' | 'bottom';

interface Props {
	handleChangeSelect?: (event: IOption) => void;
	options: IOption[];
	value?: IOption;
	defaultValue?: IOption;
	isSearchable?: boolean;
	isMulti?: boolean;
	createStyle?: any;
	controlStyle?: any;
	menuStyle?: any;
	multiValueStyle?: any;
	multiValueRemoveStyle?: any;
	optionsStyle?: any;
	Isdisable?: boolean;
	placeholder?: string;
	label?: string;
	handleBlur?: FocusEventHandler<HTMLInputElement>;
	isRequired?: boolean;
	optionsDropHeight?: number | null;
	errorMessage?: string;
	maxMenuHeight?: number | undefined;
	closeMenuOnSelect?: boolean;
	description?: string;
	footerLabel?: JSX.Element | string;
	handleFooterLabelClick?: () => void;
	isCustomComponent?: boolean;
	focusValue?: string;
	/**
	 * Render the menu into this element (normally `document.body`) with fixed
	 * positioning, so it escapes every `overflow` box between the control and
	 * the viewport. Off by default: an inline menu is right for a select in
	 * normal page flow, and only a select inside a height-capped scroll
	 * container (the flow-builder config modal's field list) needs this.
	 */
	menuPortalTarget?: HTMLElement | null;
	/**
	 * Which side of the control the menu opens on. Left unset, react-select's
	 * default 'bottom' applies and every existing dropdown behaves as it always
	 * has. 'top' is "prefer above, fall back below if it does not fit" — react-
	 * select measures the space itself and flips back rather than rendering off
	 * screen, so it is safe on a control that sometimes sits high on the page.
	 */
	menuPlacement?: IMenuType;
}

/**
 * Interface for the props of the ReactDropdown component.
 * @param {Function} Props.handleChangeSelect - Callback function invoked when the selection changes.
 * @param {IOption[]}Props.options - The list of options for the dropdown.
 * @param {IOption} Props.value - The selected value(s) in the dropdown.
 * @param {IOption} Props.defaultValue - The default value for the dropdown.
 * @param {boolean} Props.isSearchable - Flag indicating whether the dropdown is searchable.
 * @param {boolean} Props.isMulti - Flag indicating whether the dropdown supports multiple selections.
 * @param {Styles} Props.createStyle - Custom styles for the ReactSelect component.
 * @param {Styles} Props.controlStyle - Custom styles for the control section of the dropdown.
 * @param {Styles} Props.menuStyle - Custom styles for the menu section of the dropdown.
 * @param {Styles} Props.multiValueStyle - Custom styles for the multi-value section of the dropdown.
 * @param {Styles} Props.multiValueRemoveStyle - Custom styles for removing multi-value in the dropdown.
 * @param {Styles} Props.optionsStyle - Custom styles for the individual options in the dropdown.
 * @param {boolean} Props.Isdisable - Flag indicating whether the dropdown is disabled.
 * @param {string} Props.placeholder - The placeholder text for the dropdown.
 * @param {string} Props.label - The label for the dropdown.
 * @param {FocusEventHandler<HTMLInputElement>} Props.handleBlur - Callback function invoked when the dropdown loses focus.
 * @param {boolean} Props.isRequired - Flag indicating whether the dropdown is required.
 * @param {number}  Props.optionsDropHeight - The maximum height of the dropdown options menu.
 * @param {string} Props.errorMessage - Error message to display.
 * @param {string} Props.maxMenuHeight - Maximum height of the menu before scrolling
 */

export const ReactDropdown = ({
	handleChangeSelect,
	options,
	value,
	defaultValue,
	isSearchable = false,
	isMulti = false,
	createStyle,
	controlStyle = {},
	menuStyle = {},
	multiValueStyle = {},
	multiValueRemoveStyle = {},
	optionsStyle = {},
	Isdisable,
	placeholder = 'Select',
	handleBlur,
	label,
	isRequired = false,
	optionsDropHeight = null,
	errorMessage,
	maxMenuHeight = undefined,
	closeMenuOnSelect = false,
	description = '',
	footerLabel = '',
	isCustomComponent = false,
	focusValue = '',
	menuPortalTarget = null,
	menuPlacement,
}: Props) => {
	/* Every colour below is `var(--dd-*, <its old literal>)` (SIM-74). react-select
	 * styles are emotion JS, which no stylesheet theme can reach, so these literals
	 * painted a light #f5f8ff box with #000 options inside dark dialogs. Nothing
	 * defines `--dd-*` by default, so every light dropdown keeps its exact colours;
	 * the dark scopes in design-system/legacy-dark.scss define them. The new
	 * singleValue / input / placeholder entries fall back to react-select 5's own
	 * defaults (neutral80 / neutral50), so they change nothing in light either. */
	const customStyle = {
		control: (styles: { [key: string]: number | string }) => ({
			...styles,
			backgroundColor: !errorMessage ? 'var(--dd-bg, #f5f8ff)' : 'rgba(255,0,0,.06);',
			minHeight: 52,
			border: !errorMessage
				? '1px solid var(--dd-border, rgba(215, 223, 245, 1))'
				: '1px solid var(--color-error)',
			borderRadius: 8,
			fontWeight: '500',
			color: '#fff',
			boxShadow: 'none',
			'&:hover': {
				cursor: 'pointer',
				borderColor: 'var(--dd-line, #ced2de)',
			},
			'&:focus': {
				borderColor: 'red',
			},
			...controlStyle,
		}),
		menu: (styles: { [key: string]: any }) => ({
			...styles,
			background: 'var(--dd-bg, #f5f8ff)',
			borderRadius: 8,
			border: '1px solid var(--dd-line, #ced2de)',
			zIndex: 3,
			overflow: 'hidden',
			...menuStyle,
		}),
		multiValue: (styles: { [key: string]: any }) => ({
			...styles,
			backgroundColor: 'rgba(224, 233, 255, 1)',
			color: 'rgba(47, 50, 61, 1)',
			borderRadius: 50,
			paddingLeft: 8,
			paddingRight: 8,
			paddingTop: 4,
			paddingBottom: 4,
			margin: 4,
			...multiValueStyle,
		}),

		multiValueRemove: (styles: any) => ({
			...styles,
			color: 'rgba(163, 177, 214, 1)',
			borderRadius: 50,
			marginLeft: 2,
			fontSize: 12,
			...multiValueRemoveStyle,
		}),
		option: (styles: { [key: string]: any }, { data }: any) => {
			const { fontFamily } = data ?? {};
			return {
				...styles,
				fontFamily,
				backgroundColor: data.value === focusValue ? '#3c65d6' : 'var(--dd-bg, #f5f8ff)',
				color: data.value === focusValue ? '#fff' : 'var(--dd-ink, #000)',
				// height: 52,
				cursor: 'pointer',
				paddingTop: 12,
				paddingBottom: 12,
				borderBottom: '1px solid var(--dd-line, #ced2de)',
				fontWeight: '500',
				'&:hover': {
					color: '#fff',
					backgroundColor: 'var(--color-primary-light)',
				},
				'&:last-child': {
					borderBottom: 'none',
				},
				...optionsStyle,
			};
		},
		singleValue: (provided: any) => ({
			...provided,
			color: 'var(--dd-ink, hsl(0, 0%, 20%))',
		}),
		input: (provided: any) => ({
			...provided,
			color: 'var(--dd-ink, hsl(0, 0%, 20%))',
		}),
		placeholder: (provided: any) => ({
			...provided,
			color: 'var(--dd-placeholder, hsl(0, 0%, 50%))',
		}),
		valueContainer: (provided: any) => ({
			...provided,
			textOverflow: 'ellipsis',
			overflowY: 'scroll',
			display: 'flex',
			maxHeight: '130px',
		}),
		// The portal lives at <body>, so it has to out-stack whatever opened the
		// select. new-modal's overlay is zIndex 100 (modal.tsx); this sits above
		// it. Harmless when no portal target is given — the style never applies.
		menuPortal: (provided: any) => ({
			...provided,
			zIndex: 1000,
		}),
	};

	/* A fixed-position menu does not travel with its control. If the container
	 * the control sits in scrolls, the menu is left floating where it was
	 * opened — so close it on any scroll that is NOT inside the menu itself
	 * (scrolling the option list must keep it open). react-select's built-in
	 * `closeMenuOnScroll: true` only reacts to scrolling the DOCUMENT, which is
	 * never the element that scrolls here; hence the function form. Only wired
	 * when portaled: an inline menu scrolls with its container and needs none
	 * of this. */
	/* `classNamePrefix` gives the portaled menu STABLE class names
	 * (portaled-dropdown__menu-portal / __menu / __option) alongside emotion's.
	 * Two things need a hook on it: the scroll guard below, and the theme —
	 * the portal lives at <body>, outside every token scope, so a stylesheet
	 * has to reach it by class (see config-modal.scss). Emotion's own
	 * `-MenuPortal` label suffix is a dev-build artefact and not something to
	 * select on. Set only when portaled, so no other dropdown's DOM changes. */
	const portalProps = menuPortalTarget
		? {
				menuPortalTarget,
				menuPosition: 'fixed' as const,
				classNamePrefix: 'portaled-dropdown',
				closeMenuOnScroll: (event: Event) => {
					const target = event.target;
					return !(
						target instanceof Element &&
						target.closest('.portaled-dropdown__menu-portal')
					);
				},
			}
		: {};

	const scrollProps = {
		...(maxMenuHeight && {
			maxMenuHeight: maxMenuHeight,
			menuPlacement: 'auto' as IMenuType,
		}),
		// An explicit prop beats the 'auto' that maxMenuHeight brings with it.
		...(menuPlacement && { menuPlacement }),
	};

	const customComponents = {
		Menu: (props: any) => (
			<components.Menu {...props}>
				{props.children}
				{footerLabel}
			</components.Menu>
		),
	};

	return (
		<div
			id="dropdown__container"
			className={`${Isdisable ? 'dropdown__container--disabled' : ''}`}
		>
			{label && (
				<label htmlFor={label} className="input__label">
					{label}
					{isRequired && <span className="input__isRequired"> *</span>}
				</label>
			)}
			{description && (
				<div className="dropdown__container__description">{description}</div>
			)}
			<Select
				isDisabled={Isdisable}
				onBlur={handleBlur}
				defaultValue={defaultValue}
				isMulti={isMulti}
				value={value}
				placeholder={placeholder}
				className="dropdown"
				styles={createStyle ? createStyle : customStyle}
				options={options}
				isClearable={false}
				closeMenuOnSelect={closeMenuOnSelect || !isMulti}
				isSearchable={isSearchable}
				maxMenuHeight={optionsDropHeight ?? 300}
				onChange={handleChangeSelect as any}
				components={
					isCustomComponent
						? customComponents
						: { IndicatorSeparator: () => null }
				}
				{...scrollProps}
				{...portalProps}
			/>
			{/* Shahbaaz: Showing error message  */}
			{errorMessage && <span className="Error--message">{errorMessage}</span>}
		</div>
	);
};
